import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Card, CardBody, CardHeader, Input, Label, Table } from "reactstrap";

import AppModal from "../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import TelephonyOnboardingWizard from "../../../../Components/popUpComponents/TelephonyOnboarding/TelephonyOnboardingWizard";
import BusinessVerification from "./BusinessVerification";
import { telephoneNumberList } from "../../../../store/thirdPartyIntegration/action";
import {
  smsConversationApi,
  smsConversationsApi,
  smsReplyApi,
  telephoneNumberSmsSenderApi,
} from "../../../../helpers/fakebackend_helper";

// Change #100774 - "how do we buy a new number from bulkgate, just like we did
// for twilio" then "in twilio i want two way". BulkGate cannot sell numbers, so
// the number is bought from Twilio with the same wizard as Third-Party
// Integration > Phone Numbers (mobile, as only UK mobiles can text) and chosen
// here as the number texts go out from. Customers reply to it; each number that
// has replied is a conversation that can be answered from here. "Text sender
// name" keeps the old BulkGate sender (no replies possible).
export default function SmsSender() {
  const dispatch = useDispatch();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [openNumber, setOpenNumber] = useState(null);
  // Business verification (Twilio regulatory bundle). Buying a mobile waits
  // for approval; when verification is not available (older backend / delta
  // not run) buying is left as it was.
  const [verification, setVerification] = useState({ status: null, enforced: false });
  const verified = !verification.enforced || verification.status === "twilio-approved";

  const { listDataAPI, listLoading } = useSelector((state) => ({
    listDataAPI: state.ThirdPartyIntegration.telephoneNumberListData,
    listLoading: state.ThirdPartyIntegration.telephoneNumberListLoading,
  }));

  const loadConversations = async (quiet = false) => {
    if (!quiet) setConversationsLoading(true);
    try {
      const res = await smsConversationsApi();
      setConversations(res?.conversation_list || []);
    } catch (e) {
      setConversations([]);
    }
    if (!quiet) setConversationsLoading(false);
  };

  useEffect(() => {
    dispatch(telephoneNumberList());
    loadConversations();
  }, []);

  const numbers = (listDataAPI?.telephonenumberaccount_list || []).filter(
    (n) => !n.CancelledDateTime
  );
  const current = numbers.find((n) => String(n.FlagDefaultSmsSender) === "1");

  const choose = async (id) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await telephoneNumberSmsSenderApi([{ Id: id || null }]);
      if (res?.status === 200) {
        toast.success(res.message);
        if (res.Warning) toast.warning(res.Warning);
        dispatch(telephoneNumberList());
      } else {
        toast.error(res?.message || "Could not change the SMS sender");
      }
    } catch (e) {
      toast.error("Could not change the SMS sender");
    }
    setSaving(false);
  };

  return (
    <>
      <BusinessVerification
        onStatus={(status, enforced) => setVerification({ status, enforced: !!enforced })}
      />

      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h5 className="card-title mb-1">Send texts from</h5>
            <p className="text-muted mb-0">
              Send from your own mobile number so customers can reply, or keep the text
              sender name (customers cannot reply to a name).
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setWizardOpen(true)}
            disabled={!verified}
            title={verified ? undefined : "Verify your business first (above)"}
          >
            <i className="ri-add-line me-1"></i> Buy a number
          </button>
        </CardHeader>
        <CardBody>
          {listLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="d-flex flex-column gap-2">
              <div className="form-check">
                <Input
                  type="radio"
                  className="form-check-input"
                  id="smsSenderName"
                  name="smsSender"
                  checked={!current}
                  disabled={saving}
                  onChange={() => choose(null)}
                />
                <Label className="form-check-label" htmlFor="smsSenderName">
                  Text sender name <span className="text-muted">(no replies)</span>
                </Label>
              </div>
              {numbers.map((n) => {
                const smsOk = String(n.FlagSmsEnabled) === "1";
                return (
                  <div className="form-check" key={n.Id}>
                    <Input
                      type="radio"
                      className="form-check-input"
                      id={`smsSender${n.Id}`}
                      name="smsSender"
                      checked={String(current?.Id) === String(n.Id)}
                      disabled={saving || !smsOk}
                      onChange={() => choose(n.Id)}
                    />
                    <Label className="form-check-label" htmlFor={`smsSender${n.Id}`}>
                      <span className="fw-semibold">{n.TelephoneNumber}</span>
                      {n.Nickname ? <span className="text-muted"> · {n.Nickname}</span> : null}
                      {!smsOk && <span className="text-muted"> · calls only, cannot text</span>}
                    </Label>
                  </div>
                );
              })}
              {numbers.length === 0 && (
                <p className="text-muted mb-0">
                  You have no numbers yet. Buy a mobile number to send texts customers can reply to.
                </p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">Conversations</h5>
          <button className="btn btn-sm btn-soft-secondary" onClick={() => loadConversations()}>
            <i className="ri-refresh-line me-1"></i> Refresh
          </button>
        </CardHeader>
        <CardBody>
          {conversationsLoading ? (
            <LoadingSpinner />
          ) : conversations.length === 0 ? (
            <p className="text-muted mb-0">
              No replies yet. When a customer texts back to your number the conversation appears here
              and you can answer it.
            </p>
          ) : (
            <Table responsive hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Number</th>
                  <th>Last message</th>
                  <th className="text-nowrap">Received</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((c) => (
                  <tr key={c.FromNumber} role="button" onClick={() => setOpenNumber(c.FromNumber)}>
                    <td>
                      {c.Customer_FullName || <span className="text-muted">Unknown</span>}
                      {c.QtyUnread > 0 && (
                        <span className="badge bg-danger ms-2">{c.QtyUnread} new</span>
                      )}
                    </td>
                    <td className="text-nowrap">{c.FromNumber}</td>
                    <td className="text-truncate" style={{ maxWidth: 360 }}>
                      {c.LastBody}
                    </td>
                    <td className="text-nowrap">{c.LastDateTime}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <SmsConversationModal
        number={openNumber}
        senderNumber={current?.TelephoneNumber}
        onClose={() => {
          setOpenNumber(null);
          loadConversations(true);
        }}
      />

      <TelephonyOnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        entryMode="number"
        defaultNumberType="mobile"
        onComplete={() => dispatch(telephoneNumberList())}
      />
    </>
  );
}

// One conversation: every text to and from the number, and a reply box. Replies
// always go from the company's number (never the text name). Polls every 10s
// while open so a customer's answer shows up without a refresh.
function SmsConversationModal({ number, senderNumber, onClose }) {
  const [messages, setMessages] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async (quiet = false) => {
    if (!number) return;
    if (!quiet) setLoading(true);
    try {
      const res = await smsConversationApi(number);
      setMessages(res?.message_list || []);
      setCustomer(res?.Customer_Id ? { Id: res.Customer_Id, FullName: res.Customer_FullName } : null);
    } catch (e) {
      if (!quiet) setMessages([]);
    }
    if (!quiet) setLoading(false);
  };

  useEffect(() => {
    setText("");
    setMessages([]);
    setCustomer(null);
    if (!number) return undefined;
    load();
    const timer = setInterval(() => load(true), 10000);
    return () => clearInterval(timer);
  }, [number]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await smsReplyApi([
        { MobileNumber: number, Message: text.trim(), ...(customer?.Id ? { Customer_Id: customer.Id } : {}) },
      ]);
      if (res?.status === 200) {
        setText("");
        load(true);
      } else {
        toast.error(res?.message || "The reply was not sent");
      }
    } catch (e) {
      toast.error("The reply was not sent");
    }
    setSending(false);
  };

  return (
    <AppModal
      open={!!number}
      onClose={onClose}
      title={customer?.FullName || number || "Conversation"}
      subtitle={customer?.FullName ? number : undefined}
      icon="ri-message-2-line"
      size="md"
      onSubmit={send}
      submitLabel={sending ? "Sending…" : "Send reply"}
      submitDisabled={!text.trim() || sending || !senderNumber}
      cancelLabel="Close"
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 420, overflowY: "auto" }}>
          {messages.length === 0 && <p className="text-muted mb-0">No messages.</p>}
          {messages.map((m) => {
            const mine = m.Direction === "OUT";
            return (
              <div key={m.Id} className={`d-flex ${mine ? "justify-content-end" : "justify-content-start"}`}>
                <div
                  className={`px-3 py-2 rounded-3 ${mine ? "bg-primary text-white" : "bg-light"}`}
                  style={{ maxWidth: "80%", whiteSpace: "pre-wrap" }}
                >
                  {m.Body}
                  <div className={`small mt-1 ${mine ? "text-white-50" : "text-muted"}`}>
                    {m.DateTime}
                    {mine && m.FromName ? ` · from ${m.FromName}` : ""}
                    {m.Status ? ` · ${m.Status}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      )}
      {customer?.Id && (
        <p className="small mb-2">
          <a href={`/customer-single/${customer.Id}/view`}>Open customer</a>
        </p>
      )}
      <Input
        type="textarea"
        rows={3}
        value={text}
        placeholder={senderNumber ? `Reply from ${senderNumber}` : "Choose a number above to reply"}
        disabled={!senderNumber}
        onChange={(e) => setText(e.target.value)}
      />
    </AppModal>
  );
}

SmsConversationModal.propTypes = {
  number: PropTypes.string,
  senderNumber: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
